import { isRedirect, redirect } from '@tanstack/react-router'
import axios, { type AxiosError } from 'axios'

import { captureAxiosError, logSentry } from '@gto/sentry'

import { getRuntimeConfig } from '@/config/runtime-env'
import { store } from '@/store'
import {
  clearOAuthStorage,
  getOAuthClientId,
  getOAuthRedirectUri,
  getProcessedCodeKey,
  readStoredOAuthState,
  timingSafeEqual,
} from '@/utils/oauth'

type OAuthTokenResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
}

// Loader resolves either with an error result the component renders, or by
// throwing a redirect on success / already-processed code. There is no success
// shape — keep the type narrow so the renderer cannot fall into a dead branch.
export type LoaderResult = {
  status: 'error'
  message: string
}

function extractErrorMessage(error: AxiosError): string {
  if (error.response?.data != null && typeof error.response.data === 'object') {
    const data = error.response.data as Record<string, unknown>

    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
      return (data.non_field_errors as string[]).join(' ')
    }

    // django-oauth-toolkit returns `{error, error_description}` (RFC 6749 §5.2).
    // `error_description` is human-readable; `error` is the machine code
    // ("invalid_grant"), shown only as a last resort. First string-typed field
    // wins, in precedence order.
    const candidates = [data.detail, data.message, data.error_description, data.error]
    const simple = candidates.find((value): value is string => typeof value === 'string') ?? ''

    if (simple.length > 0) {
      return simple
    }
  }

  return error.message
}

export async function runOAuthCallbackLoader(search: {
  code?: string
  state?: string
}): Promise<LoaderResult> {
  if (search.code === undefined || search.code.length === 0) {
    clearOAuthStorage()

    return { status: 'error', message: '' }
  }

  // Single read: the PKCE record is parsed once and the branch below distinguishes
  // a benign timeout (expired) from a genuine mismatch / never-stored (missing).
  const stored = readStoredOAuthState()

  if (stored.status === 'expired') {
    logSentry('OAuth callback PKCE record expired before token exchange (timeout)')
    clearOAuthStorage()

    return {
      status: 'error',
      message: 'Your sign-in attempt timed out. Please try again.',
    }
  }

  if (
    stored.status === 'missing' ||
    search.state === undefined ||
    !timingSafeEqual(stored.state, search.state)
  ) {
    // Never log state values — both the stored and returned state are secrets.
    logSentry('OAuth callback state check failed: ', {
      hasStoredState: stored.status === 'valid',
      hasReturnedState: search.state !== undefined,
    })
    clearOAuthStorage()

    return {
      status: 'error',
      message: 'State mismatch — possible CSRF attempt. Please try again.',
    }
  }

  const codeVerifier = stored.codeVerifier

  const { isInitialized, initialize } = store.getState()
  if (!isInitialized) {
    await initialize()
  }

  // Already-processed code → route to dashboard instead of rendering a blank success screen.
  const processedCodeKey = getProcessedCodeKey(search.code)
  if (sessionStorage.getItem(processedCodeKey) !== null) {
    throw redirect({ to: '/dashboard', replace: true })
  }
  sessionStorage.setItem(processedCodeKey, 'true')

  try {
    // Bypass `axiosInstance` so the request interceptor doesn't inject a stale Bearer token.
    const tokenUrl = `${getRuntimeConfig().apiBaseUrl}/v4/accounts/oauth2/token/`
    const result = await axios.post<OAuthTokenResponse>(tokenUrl, {
      grant_type: 'authorization_code',
      code: search.code,
      code_verifier: codeVerifier,
      client_id: getOAuthClientId(),
      redirect_uri: getOAuthRedirectUri(),
    })

    clearOAuthStorage()

    await store.getState().onLogin(result.data.access_token, result.data.refresh_token)

    throw redirect({
      to: '/dashboard',
      replace: true,
    })
  } catch (error) {
    // TanStack's redirect() throws a tagged Response; re-throw so the router navigates.
    if (isRedirect(error)) {
      throw error
    }

    clearOAuthStorage()

    if (axios.isAxiosError(error)) {
      const message = extractErrorMessage(error)
      const status = error.response?.status

      // captureAxiosError drops 4xx, but `invalid_grant` (HTTP 400) is the most
      // common login failure we need to see — report it via logSentry. The logged
      // message is the BE's error text only; the token/code/verifier are never in it.
      if (status !== undefined && status >= 400 && status < 500) {
        logSentry('OAuth token exchange 4xx: ', message)
      } else {
        // The captured AxiosError's config.data is the token POST body, which
        // holds `code` + `code_verifier`. Redact it before handing the error to
        // Sentry — the error is terminal (we return below), so mutating is safe,
        // and URL/status/headers metadata is preserved.
        if (error.config !== undefined) {
          error.config.data = undefined
        }
        captureAxiosError(error, 'react')
      }

      return {
        status: 'error',
        message,
      }
    }

    logSentry('OAuth token exchange failed: ', error)

    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Sign in failed. Please try again.',
    }
  }
}
